/**
 * Hook para buscar alertas de estoque e validade
 * 
 * Utiliza React Query para cache e gerenciamento de estado assíncrono.
 * Retorna dados de lotes com estoque baixo ou validade próxima para exibição em tabela.
 * 
 * @module hooks/data/useEstoqueAlertas
 */

import { useQuery } from '@tanstack/react-query';
import { getEstoqueAlertas } from '../../api/api';
import { logHttpError } from '../../utils/errorLogger';
import { getErrorMessage } from '../../utils/errorMessages';

// Query key para cache do React Query
export const ESTOQUE_ALERTAS_QUERY_KEY = ['dashboard', 'estoque-alertas'];

/**
 * Hook para buscar alertas de estoque (estoque baixo e validade próxima)
 * 
 * Configurações do React Query:
 * - staleTime: 1 minuto (dados críticos, atualizados frequentemente)
 * - gcTime: 5 minutos (tempo de cache após desmontagem)
 * - retry: 3 tentativas com backoff exponencial
 * 
 * @returns {object} Objeto com dados, estados e funções
 * @property {Array} data - Array de objetos com informações de alertas (lote_id, numero_lote, data_validade, quantidade_disponivel, medicamento_nome, unidade_medida, tipo_alerta, dias_para_vencer)
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {string|null} error - Mensagem de erro (null se não houver erro)
 * @property {function} refetch - Função para recarregar os dados manualmente
 * 
 * @example
 * ```javascript
 * const { data, isLoading, error } = useEstoqueAlertas();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <AlertTable data={data} />;
 * ```
 */
export const useEstoqueAlertas = () => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ESTOQUE_ALERTAS_QUERY_KEY,
        queryFn: async () => {
            try {
                // Chama a função da API e retorna apenas a lista de dados
                const { data } = await getEstoqueAlertas();
                return data?.data || [];
            } catch (err) {
                logHttpError(err, {
                    component: 'useEstoqueAlertas',
                    endpoint: '/dashboard/estoque-alertas',
                });
                throw err;
            }
        },
        staleTime: 1000 * 60 * 2, // 2 minutos - dados críticos, mas não precisa refetch constante
        gcTime: 1000 * 60 * 5, // 5 minutos - tempo de cache (atualizado de cacheTime para gcTime)
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Manter compatibilidade: retornar error como string (se houver)
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'estoqueAlertas',
        })
        : null;

    return {
        data: data || [],
        isLoading,
        isError,
        error: errorMessage,
        refetch,
    };
};
